import React from 'react';
import * as FiIcons from 'react-icons/fi';
import {
  DestinationForm as DestinationFormData,
  DossierItem,
  PrimeIndexCategory,
  RecommendedExperience,
  RouteHighlight
} from '../../types/Destination';

const FiTrash2 = FiIcons.FiTrash2 as unknown as React.FC;

type DestinationFormProps = {
  formData: DestinationFormData;
  isEditMode?: boolean;
  canSubmit: boolean;
  submitting: boolean;
  aiFilling?: boolean;
  heroImageFile: File | null;
  seoOgImageFile: File | null;
  galleryImageFiles: Array<File | null>;
  recommendedExperienceImageFiles: Array<File | null>;
  onSubmit: (e: React.FormEvent) => void;
  onReset: () => void;
  onFieldChange: (field: keyof DestinationFormData, value: any) => void;
  onUpdateCsvField: (field: 'tags' | 'bestFor' | 'recommendedJourneySlugs', value: string) => void;
  onGenerateAiDraft: () => void;
  onHeroImageFileChange: (file: File | null) => void;
  onClearHeroImageFile: () => void;
  onSeoOgImageFileChange: (file: File | null) => void;
  onClearSeoOgImageFile: () => void;
  onGalleryImageFileChange: (idx: number, file: File | null) => void;
  onClearGalleryImageFile: (idx: number) => void;
  onRecommendedExperienceImageFileChange: (idx: number, file: File | null) => void;
  onClearRecommendedExperienceImageFile: (idx: number) => void;
};

const DestinationForm: React.FC<DestinationFormProps> = ({
  formData,
  isEditMode = false,
  canSubmit,
  submitting,
  aiFilling,
  heroImageFile,
  seoOgImageFile,
  galleryImageFiles,
  recommendedExperienceImageFiles,
  onSubmit,
  onReset,
  onFieldChange,
  onUpdateCsvField,
  onGenerateAiDraft,
  onHeroImageFileChange,
  onClearHeroImageFile,
  onSeoOgImageFileChange,
  onClearSeoOgImageFile,
  onGalleryImageFileChange,
  onClearGalleryImageFile,
  onRecommendedExperienceImageFileChange,
  onClearRecommendedExperienceImageFile
}) => {
  const updateHero = (field: 'eyebrow' | 'headline' | 'subheadline', value: string) => {
    onFieldChange('hero', { ...formData.hero, [field]: value });
  };

  const updateHeroImage = (field: 'src' | 'alt' | 'width' | 'height', value: string) => {
    onFieldChange('hero', {
      ...formData.hero,
      image: {
        ...formData.hero.image,
        [field]: field === 'width' || field === 'height' ? Number(value) || undefined : value
      }
    });
  };

  const updateSeo = (field: 'title' | 'description' | 'canonicalPath', value: string) => {
    onFieldChange('seo', { ...formData.seo, [field]: value });
  };

  const updateSeoOgImage = (field: 'src' | 'alt' | 'width' | 'height', value: string) => {
    onFieldChange('seo', {
      ...formData.seo,
      ogImage: {
        ...(formData.seo.ogImage || { src: '', alt: '' }),
        [field]: field === 'width' || field === 'height' ? Number(value) || undefined : value
      }
    });
  };

  const updateStoryItem = (idx: number, value: string) => {
    const next = [...formData.story];
    next[idx] = value;
    onFieldChange('story', next);
  };

  const addStoryItem = () => onFieldChange('story', [...formData.story, '']);
  const removeStoryItem = (idx: number) => onFieldChange('story', formData.story.filter((_, index) => index !== idx));

  const updatePrimeIndex = (field: 'score' | 'verdict' | 'quote', value: string) => {
    onFieldChange('primeIndex', {
      ...formData.primeIndex,
      [field]: field === 'score' ? Number(value) || 0 : value
    });
  };

  const updatePrimeCategory = (idx: number, field: keyof PrimeIndexCategory, value: string) => {
    const next = [...(formData.primeIndex?.categories || [])];
    next[idx] = {
      ...next[idx],
      [field]: field === 'score' ? Number(value) || 0 : value
    };
    onFieldChange('primeIndex', { ...formData.primeIndex, categories: next });
  };

  const addPrimeCategory = () => {
    onFieldChange('primeIndex', {
      ...formData.primeIndex,
      categories: [...(formData.primeIndex?.categories || []), { label: '', score: 0 }]
    });
  };

  const removePrimeCategory = (idx: number) => {
    onFieldChange('primeIndex', {
      ...formData.primeIndex,
      categories: (formData.primeIndex?.categories || []).filter((_, index) => index !== idx)
    });
  };

  const updateArrayObject = <T,>(field: keyof DestinationFormData, items: T[]) => {
    onFieldChange(field, items);
  };

  const renderObjectArray = <T extends Record<string, any>>(
    field: 'dossier' | 'routeHighlights' | 'recommendedExperiences' | 'gallery',
    title: string,
    items: T[],
    createItem: () => T,
    renderFields: (item: T, idx: number, update: (next: T) => void) => React.ReactNode
  ) => (
    <div className="form-group">
      <div className="section-bar">
        <h3>{title}</h3>
        <button
          type="button"
          className="add-variant-btn"
          onClick={() => updateArrayObject(field, [...items, createItem()])}
        >
          Add
        </button>
      </div>
      <div className="attractions-stack">
        {items.map((item, idx) => (
          <div className="attraction-card" key={`${title}-${idx}`}>
            <div className="attraction-header">
              <span className="attraction-number">{title} #{idx + 1}</span>
              <button
                type="button"
                className="remove-variant-btn"
                onClick={() => updateArrayObject(field, items.filter((_, index) => index !== idx))}
                aria-label={`Remove ${title}`}
              >
                <FiTrash2 />
              </button>
            </div>
            {renderFields(item, idx, (next) => {
              const updated = [...items];
              updated[idx] = next;
              updateArrayObject(field, updated);
            })}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <form className="destination-form" onSubmit={onSubmit}>
      <div className="section-bar">
        <h3>Website V2 Destination</h3>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Website Title *</label>
          <input type="text" value={formData.title} onChange={(e) => onFieldChange('title', e.target.value)} required />
        </div>
        <div className="form-group">
          <label>Slug *</label>
          <input type="text" value={formData.slug} onChange={(e) => onFieldChange('slug', e.target.value)} required />
        </div>
        <div className="form-group">
          <label>Country or Region *</label>
          <input type="text" value={formData.countryOrRegion} onChange={(e) => onFieldChange('countryOrRegion', e.target.value)} required />
        </div>
        <div className="form-group" style={{ display: 'flex', alignItems: 'end' }}>
          <button type="button" className="add-variant-btn" onClick={onGenerateAiDraft} disabled={aiFilling}>
            {aiFilling ? 'Generating…' : 'Generate AI Draft'}
          </button>
        </div>
      </div>

      <div className="form-group">
        <label>Summary *</label>
        <textarea rows={3} value={formData.summary} onChange={(e) => onFieldChange('summary', e.target.value)} required />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Prime Pick Tags</label>
          <input type="text" value={formData.tags.join(', ')} onChange={(e) => onUpdateCsvField('tags', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Best For</label>
          <input type="text" value={formData.bestFor.join(', ')} onChange={(e) => onUpdateCsvField('bestFor', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Recommended Journey Slugs</label>
          <input
            type="text"
            value={(formData.recommendedJourneySlugs || []).join(', ')}
            onChange={(e) => onUpdateCsvField('recommendedJourneySlugs', e.target.value)}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Best Time To Visit</label>
          <input type="text" value={formData.bestTimeToVisit} onChange={(e) => onFieldChange('bestTimeToVisit', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Ideal Duration</label>
          <input type="text" value={formData.idealDuration} onChange={(e) => onFieldChange('idealDuration', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Travel Feel</label>
          <input type="text" value={formData.travelFeel} onChange={(e) => onFieldChange('travelFeel', e.target.value)} />
        </div>
      </div>

      <div className="form-group">
        <div className="section-bar">
          <h3>Hero</h3>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Hero Image</label>
          <input type="file" accept="image/*" onChange={(e) => onHeroImageFileChange(e.target.files?.[0] || null)} />
          {heroImageFile ? (
            <div className="file-chip"><span>{heroImageFile.name}</span><button type="button" className="file-chip-remove" onClick={onClearHeroImageFile}>Remove</button></div>
          ) : formData.hero.image.src ? (
            <div className="thumbnail-preview"><img src={formData.hero.image.src} alt={formData.hero.image.alt || 'Hero preview'} /></div>
          ) : null}
        </div>
        <div className="form-group">
          <label>Hero Image Alt *</label>
          <input type="text" value={formData.hero.image.alt} onChange={(e) => updateHeroImage('alt', e.target.value)} required />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Hero Eyebrow</label>
          <input type="text" value={formData.hero.eyebrow || ''} onChange={(e) => updateHero('eyebrow', e.target.value)} />
        </div>
        <div className="form-group">
          <label>Hero Headline *</label>
          <input type="text" value={formData.hero.headline} onChange={(e) => updateHero('headline', e.target.value)} required />
        </div>
        <div className="form-group">
          <label>Hero Subheadline *</label>
          <input type="text" value={formData.hero.subheadline} onChange={(e) => updateHero('subheadline', e.target.value)} required />
        </div>
      </div>

      <div className="form-group">
        <div className="section-bar">
          <h3>Story</h3>
          <button type="button" className="add-variant-btn" onClick={addStoryItem}>
            Add Paragraph
          </button>
        </div>
        <div className="attractions-stack">
          {formData.story.map((paragraph, idx) => (
            <div className="gallery-input-row" key={`story-${idx}`}>
              <textarea rows={3} value={paragraph} onChange={(e) => updateStoryItem(idx, e.target.value)} />
              <button type="button" className="remove-variant-btn" onClick={() => removeStoryItem(idx)}>
                <FiTrash2 />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="form-group">
        <div className="section-bar">
          <h3>Prime Index</h3>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Score</label>
            <input type="number" min="0" max="100" value={formData.primeIndex?.score || 0} onChange={(e) => updatePrimeIndex('score', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Verdict</label>
            <input type="text" value={formData.primeIndex?.verdict || ''} onChange={(e) => updatePrimeIndex('verdict', e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label>Quote</label>
          <textarea rows={2} value={formData.primeIndex?.quote || ''} onChange={(e) => updatePrimeIndex('quote', e.target.value)} />
        </div>
        <div className="section-bar">
          <h3>Prime Index Categories</h3>
          <button type="button" className="add-variant-btn" onClick={addPrimeCategory}>
            Add Category
          </button>
        </div>
        <div className="attractions-stack">
          {(formData.primeIndex?.categories || []).map((item, idx) => (
            <div className="attraction-card" key={`prime-category-${idx}`}>
              <div className="attraction-header">
                <span className="attraction-number">Category #{idx + 1}</span>
                <button type="button" className="remove-variant-btn" onClick={() => removePrimeCategory(idx)}>
                  <FiTrash2 />
                </button>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Label</label>
                  <input type="text" value={item.label} onChange={(e) => updatePrimeCategory(idx, 'label', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Score</label>
                  <input type="number" min="0" max="10" step="0.1" value={item.score} onChange={(e) => updatePrimeCategory(idx, 'score', e.target.value)} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {renderObjectArray<DossierItem>(
        'dossier',
        'Dossier',
        formData.dossier || [],
        () => ({ label: '', value: '' }),
        (item, _idx, update) => (
          <div className="form-row">
            <div className="form-group">
              <label>Label</label>
              <input type="text" value={item.label} onChange={(e) => update({ ...item, label: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Value</label>
              <input type="text" value={item.value} onChange={(e) => update({ ...item, value: e.target.value })} />
            </div>
          </div>
        )
      )}

      <div className="form-group">
        <div className="section-bar">
          <h3>Editor Note</h3>
        </div>
        <div className="form-group">
          <label>Body</label>
          <textarea rows={4} value={formData.editorNote?.body || ''} onChange={(e) => onFieldChange('editorNote', { ...formData.editorNote, body: e.target.value })} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Author Name</label>
            <input type="text" value={formData.editorNote?.authorName || ''} onChange={(e) => onFieldChange('editorNote', { ...formData.editorNote, authorName: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Author Role</label>
            <input type="text" value={formData.editorNote?.authorRole || ''} onChange={(e) => onFieldChange('editorNote', { ...formData.editorNote, authorRole: e.target.value })} />
          </div>
        </div>
      </div>

      {renderObjectArray<RouteHighlight>(
        'routeHighlights',
        'Route Highlights',
        formData.routeHighlights || [],
        () => ({ title: '', stopLabel: '', description: '' }),
        (item, _idx, update) => (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Title</label>
                <input type="text" value={item.title} onChange={(e) => update({ ...item, title: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Stop Label</label>
                <input type="text" value={item.stopLabel} onChange={(e) => update({ ...item, stopLabel: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea rows={3} value={item.description || ''} onChange={(e) => update({ ...item, description: e.target.value })} />
            </div>
          </>
        )
      )}

      {renderObjectArray<RecommendedExperience>(
        'recommendedExperiences',
        'Recommended Experiences',
        formData.recommendedExperiences || [],
        () => ({ title: '', description: '', image: { src: '', alt: '' } }),
        (item, _idx, update) => (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Title</label>
                <input type="text" value={item.title} onChange={(e) => update({ ...item, title: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Image</label>
                <input type="file" accept="image/*" onChange={(e) => onRecommendedExperienceImageFileChange(_idx, e.target.files?.[0] || null)} />
                {recommendedExperienceImageFiles[_idx] ? (
                  <div className="file-chip"><span>{recommendedExperienceImageFiles[_idx]?.name}</span><button type="button" className="file-chip-remove" onClick={() => onClearRecommendedExperienceImageFile(_idx)}>Remove</button></div>
                ) : item.image?.src ? (
                  <div className="thumbnail-preview"><img src={item.image.src} alt={item.image.alt || item.title || 'Experience preview'} /></div>
                ) : null}
              </div>
              <div className="form-group">
                <label>Image Alt</label>
                <input type="text" value={item.image?.alt || ''} onChange={(e) => update({ ...item, image: { ...(item.image || { src: '', alt: '' }), alt: e.target.value } })} />
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea rows={3} value={item.description} onChange={(e) => update({ ...item, description: e.target.value })} />
            </div>
          </>
        )
      )}

      {renderObjectArray<{ src: string; alt: string; width?: number; height?: number }>(
        'gallery',
        'Gallery',
        formData.gallery || [],
        () => ({ src: '', alt: '' }),
        (item, _idx, update) => (
          <div className="form-row">
            <div className="form-group">
              <label>Image</label>
              <input type="file" accept="image/*" onChange={(e) => onGalleryImageFileChange(_idx, e.target.files?.[0] || null)} />
              {galleryImageFiles[_idx] ? (
                <div className="file-chip"><span>{galleryImageFiles[_idx]?.name}</span><button type="button" className="file-chip-remove" onClick={() => onClearGalleryImageFile(_idx)}>Remove</button></div>
              ) : item.src ? (
                <div className="thumbnail-preview"><img src={item.src} alt={item.alt || 'Gallery preview'} /></div>
              ) : null}
            </div>
            <div className="form-group">
              <label>Alt Text</label>
              <input type="text" value={item.alt} onChange={(e) => update({ ...item, alt: e.target.value })} />
            </div>
          </div>
        )
      )}

      <div className="form-group">
        <div className="section-bar">
          <h3>SEO</h3>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>SEO Title</label>
            <input type="text" value={formData.seo.title} onChange={(e) => updateSeo('title', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Canonical Path</label>
            <input type="text" value={formData.seo.canonicalPath} onChange={(e) => updateSeo('canonicalPath', e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label>SEO Description</label>
          <textarea rows={3} value={formData.seo.description} onChange={(e) => updateSeo('description', e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>OG Image</label>
            <input type="file" accept="image/*" onChange={(e) => onSeoOgImageFileChange(e.target.files?.[0] || null)} />
            {seoOgImageFile ? (
              <div className="file-chip"><span>{seoOgImageFile.name}</span><button type="button" className="file-chip-remove" onClick={onClearSeoOgImageFile}>Remove</button></div>
            ) : formData.seo.ogImage?.src ? (
              <div className="thumbnail-preview"><img src={formData.seo.ogImage.src} alt={formData.seo.ogImage.alt || 'OG preview'} /></div>
            ) : null}
          </div>
          <div className="form-group">
            <label>OG Image Alt</label>
            <input type="text" value={formData.seo.ogImage?.alt || ''} onChange={(e) => updateSeoOgImage('alt', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Published</label>
          <label className="package-editor-checkbox-row">
            <input type="checkbox" checked={Boolean(formData.isPublished)} onChange={(e) => onFieldChange('isPublished', e.target.checked)} />
            <span style={{ marginLeft: 8 }}>Make destination public</span>
          </label>
        </div>
      </div>

      <div className="form-actions">
        <button type="submit" className="submit-btn" disabled={!canSubmit || submitting}>
          {submitting ? 'Saving...' : isEditMode ? 'Update Destination' : 'Create Destination'}
        </button>
        <button type="button" className="reset-btn" onClick={onReset} disabled={submitting}>
          {isEditMode ? 'Cancel' : 'Reset'}
        </button>
      </div>
    </form>
  );
};

export default DestinationForm;
